import {
  AmbientLight,
  BoxGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  Fog,
  Group,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  SRGBColorSpace,
  TorusGeometry,
  WebGLRenderer,
  type Object3D,
} from 'three';
import type {
  ChunkSnapshot,
  EntitySnapshot,
  GamePhase,
  GameSnapshot,
  GameView,
  ObstacleKind,
  PlayerAction,
  RunnerConfig,
} from '../core/types';
import { ActionEffects } from './action-effects';
import { PrimitiveAvatarFactory } from './avatar-view';
import type { VisualPreset } from './visual-presets';

interface EntityView {
  readonly kind: ObstacleKind | 'coin';
  readonly object: Group;
}

function lerp(previous: number, current: number, alpha: number): number {
  return previous + (current - previous) * alpha;
}

function damp(current: number, target: number, responsiveness: number, delta: number): number {
  return current + (target - current) * (1 - Math.exp(-responsiveness * delta));
}

export function supportsWebGl2(): boolean {
  const canvas = document.createElement('canvas');
  return canvas.getContext('webgl2') !== null;
}

export class ThreeGameView implements GameView {
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(58, 1, 0.1, 220);
  private readonly renderer: WebGLRenderer;
  private readonly avatar: Group;
  private readonly actionEffects: ActionEffects;
  private readonly entityViews = new Map<number, EntityView>();
  private readonly chunkViews = new Map<number, Group>();
  private readonly resizeObserver: ResizeObserver;
  private readonly reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  private width = 0;
  private height = 0;
  private baseCameraFov = 58;
  private baseCameraY = 4.7;
  private baseCameraZ = 8.2;
  private smoothedFov = 58;
  private smoothedCameraX = 0;
  private smoothedCameraRoll = 0;
  private smoothedActionY = 0;
  private smoothedActionZ = 0;
  private smoothedActionLookY = 0;
  private previousRenderTime = performance.now();
  private previousAction: PlayerAction = 'grounded';
  private previousPhase: GamePhase = 'ready';
  private previousElapsed = 0;
  private landingStartedAt = Number.NEGATIVE_INFINITY;
  private collisionStartedAt = Number.NEGATIVE_INFINITY;
  private reducedMotion = this.reducedMotionQuery.matches;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly config: RunnerConfig,
    private readonly preset: VisualPreset,
  ) {
    this.avatar = new PrimitiveAvatarFactory(preset).create();
    this.actionEffects = new ActionEffects(preset.effects);
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.outputColorSpace = SRGBColorSpace;

    this.scene.background = new Color(preset.palette.background);
    this.scene.fog = new Fog(preset.palette.fog, 28, 118);
    this.scene.add(new AmbientLight(preset.palette.ambientLight, 1.65));
    const sun = new DirectionalLight(preset.palette.keyLight, 2.6);
    sun.position.set(-5, 9, 6);
    this.scene.add(sun);

    const ground = new Mesh(new PlaneGeometry(150, 240), this.material(preset.palette.ground));
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(0, -0.3, -80);
    this.scene.add(ground);

    this.scene.add(this.avatar);
    this.scene.add(this.actionEffects.group);
    this.resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this.reducedMotionQuery.addEventListener('change', this.onMotionPreferenceChange);
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  public render(snapshot: Readonly<GameSnapshot>, alpha: number): void {
    const now = performance.now();
    if (snapshot.elapsed < this.previousElapsed) {
      this.landingStartedAt = Number.NEGATIVE_INFINITY;
      this.collisionStartedAt = Number.NEGATIVE_INFINITY;
    }
    if (this.previousAction === 'jumping' && snapshot.player.action === 'grounded') {
      this.landingStartedAt = now;
    }
    if (this.previousPhase === 'running' && snapshot.phase === 'gameOver') {
      this.collisionStartedAt = now;
    }

    this.syncChunks(snapshot.chunks, alpha);
    this.syncEntities(snapshot.entities, alpha);
    this.syncAvatar(snapshot, alpha);
    this.actionEffects.update(snapshot);
    this.updateCamera(snapshot, alpha, now);
    this.renderer.render(this.scene, this.camera);
    this.previousAction = snapshot.player.action;
    this.previousPhase = snapshot.phase;
    this.previousElapsed = snapshot.elapsed;
  }

  public dispose(): void {
    this.resizeObserver.disconnect();
    this.reducedMotionQuery.removeEventListener('change', this.onMotionPreferenceChange);
    this.scene.traverse((object) => {
      this.disposeObject(object);
    });
    this.renderer.dispose();
  }

  private readonly onMotionPreferenceChange = (event: MediaQueryListEvent): void => {
    this.reducedMotion = event.matches;
  };

  private updateCamera(snapshot: Readonly<GameSnapshot>, alpha: number, now: number): void {
    const delta = Math.min(0.05, Math.max(0, (now - this.previousRenderTime) / 1000));
    this.previousRenderTime = now;
    const playerX = lerp(snapshot.player.previousX, snapshot.player.x, alpha);
    const speedRange = Math.max(0.001, this.config.maxSpeed - this.config.baseSpeed);
    const speedIntensity = Math.min(
      1,
      Math.max(0, (snapshot.speed - this.config.baseSpeed) / speedRange),
    );
    const motionEnabled = !this.reducedMotion;
    const cameraStyle = this.preset.camera;
    const jumpProgress = Math.min(
      1,
      Math.max(0, snapshot.player.y / cameraStyle.jumpReferenceHeight),
    );
    const targetActionY = motionEnabled
      ? snapshot.player.action === 'jumping'
        ? jumpProgress * cameraStyle.jumpLift
        : snapshot.player.action === 'sliding'
          ? -cameraStyle.slideDrop
          : 0
      : 0;
    const targetActionZ = motionEnabled
      ? snapshot.player.action === 'jumping'
        ? jumpProgress * cameraStyle.jumpPullback
        : snapshot.player.action === 'sliding'
          ? -cameraStyle.slidePushIn
          : 0
      : 0;
    const targetActionLookY = motionEnabled
      ? snapshot.player.action === 'jumping'
        ? jumpProgress * cameraStyle.jumpLookLift
        : snapshot.player.action === 'sliding'
          ? -cameraStyle.slideLookDrop
          : 0
      : 0;
    const actionFov = motionEnabled
      ? snapshot.player.action === 'jumping'
        ? jumpProgress * -cameraStyle.jumpFovNarrowing
        : snapshot.player.action === 'sliding'
          ? cameraStyle.slideFovBoost
          : 0
      : 0;
    const targetFov =
      this.baseCameraFov +
      (snapshot.phase === 'running' && motionEnabled
        ? cameraStyle.speedFovBoost + speedIntensity * cameraStyle.speedFovRange
        : 0) +
      actionFov;
    const laneDirection = snapshot.player.laneChanging
      ? Math.sign(snapshot.player.x - snapshot.player.previousX)
      : 0;
    const targetRoll = motionEnabled ? laneDirection * -cameraStyle.laneRoll : 0;
    const targetCameraX = motionEnabled ? playerX * cameraStyle.laneFollow : 0;

    this.smoothedFov = damp(this.smoothedFov, targetFov, cameraStyle.fovResponsiveness, delta);
    this.smoothedCameraX = damp(
      this.smoothedCameraX,
      targetCameraX,
      cameraStyle.actionResponsiveness,
      delta,
    );
    this.smoothedCameraRoll = damp(
      this.smoothedCameraRoll,
      targetRoll,
      laneDirection === 0 ? cameraStyle.laneReturnResponsiveness : cameraStyle.laneResponsiveness,
      delta,
    );
    this.smoothedActionY = damp(
      this.smoothedActionY,
      targetActionY,
      cameraStyle.actionResponsiveness,
      delta,
    );
    this.smoothedActionZ = damp(
      this.smoothedActionZ,
      targetActionZ,
      cameraStyle.actionResponsiveness,
      delta,
    );
    this.smoothedActionLookY = damp(
      this.smoothedActionLookY,
      targetActionLookY,
      cameraStyle.actionResponsiveness,
      delta,
    );

    let landingY = 0;
    let landingZ = 0;
    if (motionEnabled) {
      const landingProgress = (now - this.landingStartedAt) / (cameraStyle.landingDuration * 1000);
      if (landingProgress >= 0 && landingProgress < 1) {
        const decay = 1 - landingProgress;
        landingY =
          -Math.sin(landingProgress * Math.PI * 2.5) * cameraStyle.landingVerticalImpulse * decay;
        landingZ = Math.sin(landingProgress * Math.PI) * cameraStyle.landingDepthImpulse * decay;
      }
    }

    let shakeX = 0;
    let shakeY = 0;
    let shakeRoll = 0;
    let impactFov = 0;
    if (motionEnabled) {
      const collisionProgress =
        (now - this.collisionStartedAt) / (cameraStyle.collisionDuration * 1000);
      if (collisionProgress >= 0 && collisionProgress < 1) {
        const decay = (1 - collisionProgress) ** 2;
        shakeX = Math.sin(collisionProgress * 97) * cameraStyle.collisionShakeX * decay;
        shakeY = Math.sin(collisionProgress * 71) * cameraStyle.collisionShakeY * decay;
        shakeRoll = Math.sin(collisionProgress * 83) * cameraStyle.collisionRoll * decay;
        impactFov = cameraStyle.collisionFovKick * decay;
      }
    }

    this.camera.position.set(
      this.smoothedCameraX + shakeX,
      this.baseCameraY + this.smoothedActionY + landingY + shakeY,
      this.baseCameraZ + this.smoothedActionZ + landingZ,
    );
    this.camera.fov = this.smoothedFov + impactFov;
    this.camera.lookAt(
      this.smoothedCameraX * cameraStyle.laneLookFollow,
      1.1 + this.smoothedActionLookY + landingY * cameraStyle.landingLookInfluence,
      -9.5,
    );
    this.camera.rotateZ(this.smoothedCameraRoll + shakeRoll);
    this.camera.updateProjectionMatrix();
  }

  private syncAvatar(snapshot: Readonly<GameSnapshot>, alpha: number): void {
    const x = lerp(snapshot.player.previousX, snapshot.player.x, alpha);
    const y = lerp(snapshot.player.previousY, snapshot.player.y, alpha);
    this.avatar.position.set(x, y, 0);
    this.avatar.scale.set(
      snapshot.player.action === 'sliding' ? 1.12 : 1,
      snapshot.player.action === 'sliding' ? 0.52 : snapshot.player.action === 'jumping' ? 1.06 : 1,
      1,
    );
    this.avatar.rotation.x = snapshot.player.action === 'sliding' ? -0.2 : 0;
    this.avatar.rotation.z = snapshot.player.laneChanging ? (snapshot.player.x - x) * -0.08 : 0;

    const runCycle = snapshot.distance * 1.4;
    const swing = snapshot.player.action === 'grounded' ? Math.sin(runCycle) * 0.52 : 0;
    this.avatar.getObjectByName('left-leg')?.rotation.set(swing, 0, 0);
    this.avatar.getObjectByName('right-leg')?.rotation.set(-swing, 0, 0);
    this.avatar.getObjectByName('left-arm')?.rotation.set(-swing * 0.65, 0, -0.18);
    this.avatar.getObjectByName('right-arm')?.rotation.set(swing * 0.65, 0, 0.18);
    const shadow = this.avatar.getObjectByName('shadow');
    if (shadow !== undefined) shadow.position.y = -y + 0.015;
  }

  private syncChunks(chunks: readonly ChunkSnapshot[], alpha: number): void {
    const activeIds = new Set<number>();
    for (const chunk of chunks) {
      const view = this.chunkViews.get(chunk.id) ?? this.createChunkView(chunk);
      view.visible = chunk.active;
      if (!chunk.active) continue;
      activeIds.add(chunk.id);
      view.position.z = lerp(chunk.previousZ, chunk.z, alpha);
    }
    for (const [id, view] of this.chunkViews) {
      if (!activeIds.has(id)) view.visible = false;
    }
  }

  private syncEntities(entities: readonly EntitySnapshot[], alpha: number): void {
    const activeIds = new Set<number>();
    for (const entity of entities) {
      let view = this.entityViews.get(entity.id);
      if (view?.kind !== entity.kind) {
        if (view !== undefined) {
          this.scene.remove(view.object);
          view.object.traverse((object) => {
            this.disposeObject(object);
          });
        }
        view = { kind: entity.kind, object: this.createEntityObject(entity.kind) };
        this.entityViews.set(entity.id, view);
        this.scene.add(view.object);
      }
      view.object.visible = entity.active;
      if (!entity.active) continue;
      activeIds.add(entity.id);
      view.object.position.set(
        this.config.laneCenters[entity.lane],
        0,
        lerp(entity.previousZ, entity.z, alpha),
      );
      if (entity.kind === 'coin') view.object.rotation.y = entity.z * 0.11;
    }
    for (const [id, view] of this.entityViews) {
      if (!activeIds.has(id)) view.object.visible = false;
    }
  }

  private createChunkView(chunk: ChunkSnapshot): Group {
    const group = new Group();
    const road = new Mesh(
      new BoxGeometry(8.3, 0.28, chunk.length - 0.2),
      this.material(this.preset.palette.track),
    );
    road.position.y = -0.16;
    group.add(road);

    const stripeMaterial = this.material(this.preset.palette.laneStripe);
    for (const x of [-1.2, 1.2]) {
      for (let z = -chunk.length / 2 + 2; z < chunk.length / 2; z += 4) {
        const stripe = new Mesh(new BoxGeometry(0.05, 0.02, 1.4), stripeMaterial);
        stripe.position.set(x, 0.005, z);
        group.add(stripe);
      }
    }
    for (const x of [-4.35, 4.35]) {
      const rail = new Mesh(
        new BoxGeometry(0.2, 0.16, chunk.length),
        this.material(this.preset.palette.rail),
      );
      rail.position.set(x, 0.02, 0);
      group.add(rail);
    }
    this.chunkViews.set(chunk.id, group);
    this.scene.add(group);
    return group;
  }

  private createEntityObject(kind: ObstacleKind | 'coin'): Group {
    const group = new Group();
    if (kind === 'coin') {
      const coin = new Mesh(
        new TorusGeometry(
          0.28,
          0.1,
          this.preset.materials.geometrySegments,
          this.preset.materials.geometrySegments * 2,
        ),
        this.material(this.preset.palette.coin),
      );
      coin.position.y = 1.05;
      group.add(coin);
      return group;
    }

    if (kind === 'barrier') {
      const orange = this.material(this.preset.palette.barrier);
      const body = new Mesh(new BoxGeometry(1.55, 0.75, 0.52), orange);
      body.position.y = 0.38;
      group.add(body);
      for (const x of [-0.55, 0.55]) {
        const marker = new Mesh(
          new BoxGeometry(0.18, 0.95, 0.58),
          this.material(this.preset.palette.barrierAccent),
        );
        marker.position.set(x, 0.47, 0);
        marker.rotation.z = 0.3;
        group.add(marker);
      }
      return group;
    }

    if (kind === 'beam') {
      const teal = this.material(this.preset.palette.beam);
      for (const x of [-0.72, 0.72]) {
        const post = new Mesh(new CylinderGeometry(0.1, 0.14, 2.3, 6), teal);
        post.position.set(x, 1.15, 0);
        group.add(post);
      }
      const crossbar = new Mesh(new BoxGeometry(1.8, 0.45, 0.58), teal);
      crossbar.position.y = 1.45;
      group.add(crossbar);
      return group;
    }

    const red = this.material(this.preset.palette.blocker);
    const wall = new Mesh(new BoxGeometry(1.75, 2.5, 0.62), red);
    wall.position.y = 1.25;
    group.add(wall);
    const inset = new Mesh(
      new BoxGeometry(1.2, 1.2, 0.67),
      this.material(this.preset.palette.blockerInset),
    );
    inset.position.y = 1.25;
    inset.rotation.z = Math.PI / 4;
    group.add(inset);
    return group;
  }

  private resize(): void {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    const portrait = this.camera.aspect < 0.75;
    this.baseCameraFov = portrait ? 68 : 54;
    this.baseCameraY = portrait ? 5.1 : 4.7;
    this.baseCameraZ = portrait ? 10.2 : 8.2;
    this.smoothedFov = this.baseCameraFov;
    this.camera.fov = this.baseCameraFov;
    this.camera.position.set(0, this.baseCameraY, this.baseCameraZ);
    this.camera.lookAt(0, 1.1, -9.5);
    this.camera.updateProjectionMatrix();
  }

  private material(color: number): MeshStandardMaterial {
    return new MeshStandardMaterial({
      color,
      roughness: this.preset.materials.roughness,
      metalness: this.preset.materials.metalness,
      flatShading: this.preset.materials.flatShading,
    });
  }

  private disposeObject(object: Object3D): void {
    if (object.type !== 'Mesh') return;
    const mesh = object as Mesh;
    mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const item of materials) item.dispose();
  }
}
